/* eslint-disable react/jsx-pascal-case */
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import * as BSL_Common from "../../components/BSL_CommonControls";
import BSL_Button from "../../components/BSL_Button";
import BSL_Dialog from "../../components/BSL_Dialog";
import * as APP from "../../redux_store/appState";
import { ClearScratch } from "../layout/PaperDesigner";
import {
    updateAreaSpecification,
    updateAreaSpecificationModificationArray,
    deleteFromAreaSpecificationModificationArray,
} from "../../redux_store/jobState";
import BSL_UploadDocument from "../../components/BSL_Upload/BSL_UploadDocument";
import Message from "../../components/Message";
import { FormControl, FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import { SHOW_DESIGNER_MESSAGE } from "../../redux_store/jobState_Validation";

const useStyles = makeStyles({
    root: {},
});

// tiny global flag key — used to indicate we're in "moving a note" mode
const MOVING_FLAG = "__BSL_specNotes_is_moving";
// global dialog-open flag + event
const OPENED_FLAG = "__BSL_specNotes_dialog_open";
const OPENED_EVENT = "BSL_SPECNOTES_DIALOG_OPEN_CHANGED";

function isDialogOpen() {
    try {
        return !!(typeof window !== "undefined" && window[OPENED_FLAG]);
    } catch (e) {
        return false;
    }
}
function setDialogOpen(val) {
    try {
        if (typeof window !== "undefined") {
            window[OPENED_FLAG] = !!val;
            try {
                window.dispatchEvent(new CustomEvent(OPENED_EVENT, { detail: !!val }));
            } catch (e) {
                // ignore
            }
        }
    } catch (e) {}
}
// expose for debugging if needed
if (typeof window !== "undefined") {
    window.isSpecNotesDialogOpen = isDialogOpen;
    window.setSpecNotesDialogOpen = setDialogOpen;
}

export default function SpecNotesDialog(props) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { edit_note, areaIndex } = props;
    const app = useSelector((state) => state.app);
    const AREA_INDEX = app.designer.designer2D?.[APP.AREA_INDEX];
    const job = useSelector((state) => state.job);
    const SELECTED_POINTS = app.designer.designer2D[APP.SELECTED_POINT];
    const SELECT_POINT = app.designer.designer2D[APP.SELECT_POINT];
    const SELECT_POINT_NOTE_MESSAGE = app.designer.designer2D[APP.SELECT_POINT_NOTE];

    const suppressOnCloseRef = React.useRef(false);
    const editingRefRef = React.useRef(null);
    const skipOpenRef = React.useRef(false);

    // local guard (still useful) to avoid duplicate opens within same instance
    const openedRef = React.useRef(false);

    const [storedNote, setStoredNote] = React.useState(null);
    const [showNoteDialog, setShowNoteDialog] = React.useState(edit_note && !storedNote ? true : false);
    const [waitingForPoints, setWaitingForPoints] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);
    const [showMessage, setShowMessage] = React.useState(null);
    const [imageName, setImageName] = React.useState(edit_note?.Image ? edit_note.Image : "none");

    let edit_note_text = "";
    if (edit_note) {
        for (let i = 1; i < 10; i++) {
            let text = edit_note["Note" + (i > 1 ? i : "")];
            if (text != undefined && text != null) edit_note_text += (edit_note_text === "" ? "" : "\n") + text;
        }
    }

    const [inputValue, setInputValue] = React.useState(edit_note_text.replace("📷 ", ""));
    const [showArrow, setShowArrow] = React.useState(edit_note?.showArrow ?? true);

    // initialize waitingFromPoints once on mount from global moving flag if present
    React.useEffect(() => {
        try {
            if (typeof window !== "undefined" && window[MOVING_FLAG] === false) setWaitingForPoints(true);
        } catch (e) {
            /* ignore */
        }
    }, [window[MOVING_FLAG]]); // run once

    // keep global OPENED_FLAG in sync with local showNoteDialog changes
    React.useEffect(() => {
        if (showNoteDialog) {
            // if dialog opened locally, mark global flag (if not already)
            if (!isDialogOpen()) setDialogOpen(true);
            openedRef.current = true;
        } else {
            // dialog closed locally -> clear global flag
            if (isDialogOpen()) setDialogOpen(false);
            openedRef.current = false;
        }
    }, [showNoteDialog]);

    // If other parts of the app change the global open flag, ensure local ref clears
    React.useEffect(() => {
        const handler = (e) => {
            // if global was cleared externally, ensure local open guard cleared
            if (!e?.detail) {
                openedRef.current = false;
                setShowNoteDialog(false);
            }
        };
        try {
            window.addEventListener(OPENED_EVENT, handler);
        } catch (e) {}
        return () => {
            try {
                window.removeEventListener(OPENED_EVENT, handler);
            } catch (e) {}
        };
    }, []);

    React.useEffect(() => {
        if (skipOpenRef.current) {
            skipOpenRef.current = false;
            return;
        }

        if (!waitingForPoints || !SELECTED_POINTS) return;

        // finish a move/create if this instance has the storedNote
        if (storedNote) {
            try {
                let p1 = { ...SELECTED_POINTS[0] };
                let p2 = { ...SELECTED_POINTS[1] };

                if (job?.AreaSpecifications?.[areaIndex > -1 ? areaIndex : 0]?.SHAPE?.RotateArea === 1) {
                    p1.y = SELECTED_POINTS[0].x;
                    p1.x = -SELECTED_POINTS[0].y;
                    p2.y = SELECTED_POINTS[1].x;
                    p2.x = -SELECTED_POINTS[1].y;
                }

                let ref = "(" + p1.x.toFixed(2) + "/" + p1.y.toFixed(2) + ")";
                let new_note = {
                    Ref: ref,
                    Position1: "(" + p1.x.toFixed(2) + "," + p1.y.toFixed(2) + ",0.00)",
                    Position2: "(" + p2.x.toFixed(2) + "," + p2.y.toFixed(2) + ",0.00)",
                    Image: storedNote.imageName ?? storedNote.Image ?? "none",
                    Note: storedNote.Note ?? storedNote.Note1 ?? "",
                    showArrow: storedNote.showArrow !== undefined ? storedNote.showArrow : true,
                };

                dispatch(
                    updateAreaSpecificationModificationArray(
                        AREA_INDEX > -1 ? AREA_INDEX : areaIndex,
                        "DrawingNotes",
                        new_note.Ref,
                        null,
                        new_note,
                        true
                    )
                );

                if (editingRefRef.current) {
                    dispatch(
                        deleteFromAreaSpecificationModificationArray(
                            AREA_INDEX > -1 ? AREA_INDEX : areaIndex,
                            "DrawingNotes",
                            editingRefRef.current
                        )
                    );
                    editingRefRef.current = null;
                }

                // clear moving flag now that move finished
                try {
                    if (typeof window !== "undefined") {
                        window[MOVING_FLAG] = false;
                        setWaitingForPoints(true);
                    }
                } catch (e) {
                    /* ignore */
                }

                skipOpenRef.current = true;

                // We are still editing
                if (SELECT_POINT_NOTE_MESSAGE !== null) {
                    ClearScratch();
                    setInputValue("");
                    setShowArrow(false);
                    setShowMessage("");
                    setShowNoteDialog(false);
                    openedRef.current = false;
                    setDialogOpen(false);

                    setWaitingForPoints(true);
                    dispatch(APP.update2DDesigner(APP.SELECT_POINT, 0));
                    dispatch(APP.update2DDesigner(APP.SELECT_POINT, "2 Arrow"));
                    dispatch(APP.update2DDesigner(APP.SELECT_POINT_ALLOW_HIT_SELTYPE, "note"));
                    return;
                }

                dispatch(APP.update2DDesigner(APP.SELECT_POINT, 0));
                setWaitingForPoints(true);
                ClearScratch();
                setStoredNote(null);
                editingRefRef.current = null;

                setInputValue("");
                setShowArrow(true);
                setStoredNote(null);
            } catch (err) {
                setShowMessage({ type: "error", message: "Failed to create note from picked points." });
                console.error("SpecNotesDialog - create from points error:", err);
                // ensure flag cleared on error so UI recovers
                try {
                    if (typeof window !== "undefined") window[MOVING_FLAG] = false;
                } catch (e) {}
            }

            return;
        }

        // Normal add-note flow: if a move is happening globally, suppress the popup
        let globalMoving = false;
        try {
            globalMoving = !!(typeof window !== "undefined" && window[MOVING_FLAG]);
        } catch (e) {
            globalMoving = false;
        }
        if (globalMoving) {
            // another instance is moving — don't open text dialog here
            setWaitingForPoints(false);
            dispatch(APP.update2DDesigner(APP.SELECT_POINT, 0));
            setInputValue("");
            return;
        } else {
            setWaitingForPoints(true);
        }

        // Normal open dialog — do not try to open if another instance already opened it
        setWaitingForPoints(false);
        dispatch(APP.update2DDesigner(APP.SELECT_POINT, 0));
        setInputValue("");

        if (isDialogOpen()) {
            // already opened elsewhere — skip
        } else {
            // mark globally first to prevent race
            setDialogOpen(true);
            openedRef.current = true;
            setShowNoteDialog(true);
        }
    }, [SELECTED_POINTS]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (edit_note?.showArrow !== undefined) {
            setShowArrow(edit_note.showArrow);
        }
    }, [edit_note]);

    React.useEffect(() => {
        if (AREA_INDEX !== areaIndex && job.AreaSpecifications?.length > 1) return;
        else if (AREA_INDEX !== areaIndex) return;
        if (!loaded) return;
        if (SELECT_POINT === false && !edit_note) {
            setWaitingForPoints(false);
            dispatch(APP.updateDesigner(APP.TOOL_DRAWER_HIDE, false));
            if (job.AreaSpecifications.length > 1) {
                dispatch(APP.setDesignerLoadingBackdrop("Loading Full Layout..."));
                dispatch(APP.update2DDesigner(APP.AREA_INDEX, -1));
            }
        }
    }, [SELECT_POINT]);

    const getNotePoints = () => {
        setWaitingForPoints(true);
        ClearScratch();
        setStoredNote(null);
        editingRefRef.current = null;

        if (job.AreaSpecifications?.length > 1) {
            dispatch(APP.update2DDesigner(APP.AREA_INDEX, areaIndex));
            dispatch(APP.setDesignerLoadingBackdrop("Loading Area " + (areaIndex + 1) + " Layout..."));
        }
        dispatch(
            APP.update2DDesigner(
                APP.SELECT_POINT_NOTE,
                <>
                    First, click a point to set where the arrow for the note will point to (each note has an arrow).
                    <br />
                    Next, click where you would like to add the text of the note
                    <br />
                    *Right click or press 'ESC' to cancel.
                </>
            )
        );
        dispatch(APP.updateDesigner(APP.TOOL_DRAWER_HIDE, true));
        dispatch(APP.update2DDesigner(APP.SELECT_POINT, 0));
        dispatch(APP.update2DDesigner(APP.SELECT_POINT, "2 Arrow"));
        dispatch(APP.update2DDesigner(APP.SELECT_POINT_ALLOW_HIT_SELTYPE, "note"));
    };

    const wipeNotes = () => {
        dispatch(updateAreaSpecification(AREA_INDEX > -1 ? AREA_INDEX : areaIndex, "DrawingNotes", null, []));
    };

    const onClose = () => {
        // clear moving flag if user cancels while moving
        try {
            if (typeof window !== "undefined") window[MOVING_FLAG] = false;
        } catch (e) {}

        ClearScratch();
        setInputValue("");
        setShowArrow(false);
        setShowMessage("");
        setShowNoteDialog(false);
        openedRef.current = false; // clear local guard
        setDialogOpen(false); // clear global flag

        if (props.onClose) {
            props.onClose();
            return;
        }

        setWaitingForPoints(true);
        dispatch(APP.update2DDesigner(APP.SELECT_POINT, "2 Arrow"));
    };

    if (!loaded) setLoaded(true);

    const handleSaveClick = () => {
        if (showArrow === false && inputValue.trim() === "" && (!imageName || imageName === "none")) {
            setShowMessage({
                type: "error",
                message: "Can not have empty note and no arrow.",
            });
            return;
        }

        let new_note;
        if (edit_note && !editingRefRef.current) {
            new_note = { ...edit_note };
        } else {
            let p1 = SELECTED_POINTS && SELECTED_POINTS[0] ? { ...SELECTED_POINTS[0] } : { x: 0, y: 0 };
            let p2 = SELECTED_POINTS && SELECTED_POINTS[1] ? { ...SELECTED_POINTS[1] } : { x: 0, y: 0 };

            if (job?.AreaSpecifications?.[areaIndex > -1 ? areaIndex : 0]?.SHAPE?.RotateArea === 1 && SELECTED_POINTS) {
                p1.y = SELECTED_POINTS[0].x;
                p1.x = -SELECTED_POINTS[0].y;
                p2.y = SELECTED_POINTS[1].x;
                p2.x = -SELECTED_POINTS[1].y;
            }

            let ref = "(" + p1.x.toFixed(2) + "/" + p1.y.toFixed(2) + ")";
            new_note = {
                Ref: ref,
                Position1: "(" + p1.x.toFixed(2) + "," + p1.y.toFixed(2) + ",0.00)",
                Position2: "(" + p2.x.toFixed(2) + "," + p2.y.toFixed(2) + ",0.00)",
                Image: imageName ?? "none",
            };
        }

        let text = inputValue.trim();
        if (imageName && !imageName.includes("none")) text = "📷 " + text;
        let splitted = text.trim() ? text.trim().split("\n") : [];
        let i = 1;
        for (let str of splitted) {
            new_note["Note" + (i > 1 ? i : "")] = str;
            i++;
        }
        new_note.Image = imageName ?? "none";
        new_note.showArrow = showArrow;

        dispatch(
            updateAreaSpecificationModificationArray(
                AREA_INDEX > -1 ? AREA_INDEX : areaIndex,
                "DrawingNotes",
                new_note.Ref,
                null,
                new_note,
                true
            )
        );

        if (editingRefRef.current) {
            dispatch(
                deleteFromAreaSpecificationModificationArray(
                    AREA_INDEX > -1 ? AREA_INDEX : areaIndex,
                    "DrawingNotes",
                    editingRefRef.current
                )
            );
            editingRefRef.current = null;
        }

        setInputValue("");
        setShowArrow(true);
        setStoredNote(null);

        // onClose will clear the global flag too
        onClose(false);
    };

    const handleEditPosition = () => {
        if (SELECT_POINT_NOTE_MESSAGE === null) {
            getNotePoints();
        }
        if (!edit_note) return;

        dispatch({
            type: SHOW_DESIGNER_MESSAGE,
            message:
                "You are modifing the position of note: " +
                edit_note?.Note +
                " | first click where to set arrow, followed by the tail.",
        });

        // set global moving flag so other instances suppress opening
        try {
            if (typeof window !== "undefined") window[MOVING_FLAG] = true;
        } catch (e) {}

        editingRefRef.current = edit_note.Ref;

        ClearScratch();
        dispatch(APP.update2DDesigner(APP.SELECT_POINT, 0));

        suppressOnCloseRef.current = true;
        setStoredNote(edit_note);
        setShowNoteDialog(false);
        openedRef.current = false; // clear local guard
        setDialogOpen(false); // clear global flag so editing flow can control it again
        setWaitingForPoints(true);

        dispatch(APP.update2DDesigner(APP.SELECT_POINT, "2 Arrow"));
        dispatch(APP.update2DDesigner(APP.SELECT_POINT_ALLOW_HIT_SELTYPE, "note"));
    };

    return (
        <>
            {!edit_note && (
                <BSL_Common.SectionRow>
                    <BSL_Button
                        style={{ marginLeft: "8px", alignSelf: "center", width: "60%" }}
                        onClick={getNotePoints}
                        disabled={app.designer.threeDMode && !app.designer.threeDMiniMode}
                        disabledTooltip="Cannot add drawing notes in 3D Model View"
                    >
                        Add Drawing Notes {app.designer.threeDMode && !app.designer.threeDMiniMode ? "(Plan Only)" : ""}
                    </BSL_Button>
                    <BSL_Button
                        secondary
                        style={{ marginLeft: "8px", alignSelf: "center", flex: 1 }}
                        onClick={wipeNotes}
                    >
                        Wipe All (Area)
                    </BSL_Button>
                </BSL_Common.SectionRow>
            )}
            {showNoteDialog && (
                <BSL_Dialog
                    title={edit_note ? "Edit Drawing Note" : "Add Drawing Note"}
                    open={showNoteDialog}
                    allowMultilineInput
                    inputPlaceholder="Leave blank for arrow"
                    inputValue={inputValue}
                    inputOnChange={setInputValue}
                    onClose={onClose}
                    button1Text={edit_note ? "Edit Note" : "Save Note"}
                    button1Click={handleSaveClick}
                    button2Text="Cancel"
                    button2Click={onClose}
                    button3Text={edit_note ? "Delete Note" : null}
                    button3Click={() => {
                        onClose();
                        if (edit_note) {
                            dispatch(
                                deleteFromAreaSpecificationModificationArray(
                                    AREA_INDEX > -1 ? AREA_INDEX : areaIndex,
                                    "DrawingNotes",
                                    edit_note.Ref
                                )
                            );
                        }
                    }}
                    button4Text={edit_note ? "Edit Position" : null}
                    button4Click={edit_note ? handleEditPosition : null}
                >
                    <BSL_UploadDocument
                        buttonText="IMPORT PHOTO..."
                        backgroundText="ATTACH A PHOTO"
                        onSuccess={(message) => {
                            setImageName(message.filename);
                            setShowMessage(message);
                        }}
                        onError={setShowMessage}
                        onClear={() => {
                            setImageName("none");
                        }}
                        documentFolder={job.id}
                        documentFilename={imageName}
                        sizeLimitMB={2}
                    />

                    <FormControl>
                        <RadioGroup
                            row
                            style={{ flexDirection: "column" }}
                            value={showArrow ? "With Arrow" : "No Arrow"}
                            onChange={(event) => setShowArrow(event.target.value === "With Arrow")}
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                        >
                            <FormControlLabel
                                key="With Arrow"
                                value="With Arrow"
                                control={<Radio />}
                                label="With Arrow"
                            />
                            <FormControlLabel key="No Arrow" value="No Arrow" control={<Radio />} label="No Arrow" />
                        </RadioGroup>
                    </FormControl>

                    <Message
                        open={!!showMessage}
                        message={showMessage?.message}
                        type={showMessage?.type}
                        onClose={() => setShowMessage(null)}
                    />
                </BSL_Dialog>
            )}
        </>
    );
}
